import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
  FaBuilding,
  FaCalendarCheck,
  FaClock,
  FaExclamationTriangle,
  FaSearch,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";

import attendanceServices from "../../../services/attendanceServices";
import useScrollToTop from "../../../hooks/useScollToTop";

import AttendanceChart from "./AttendanceChart";
import AttendanceOverviewStats from "./components/AttendanceOverviewStats";
import AttendanceFilters from "./components/AttendanceFilters";
import AttendanceClubCard from "./components/AttendanceClubCard";

dayjs.locale("fr");

const nf = new Intl.NumberFormat("fr-FR");

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : "—";
}

function getActivityMeta(lastCall) {
  if (!lastCall) {
    return {
      key: "none",
      label: "Jamais utilisé",
      tone: "slate",
      daysSince: null,
      lastCallLabel: "Aucun appel enregistré",
      sortValue: Number.MAX_SAFE_INTEGER,
    };
  }

  const d = dayjs(lastCall);
  if (!d.isValid()) {
    return {
      key: "none",
      label: "Inconnu",
      tone: "slate",
      daysSince: null,
      lastCallLabel: "Date invalide",
      sortValue: Number.MAX_SAFE_INTEGER,
    };
  }

  const daysSince = Math.max(
    dayjs().startOf("day").diff(d.startOf("day"), "day"),
    0
  );

  if (daysSince <= 7) {
    return {
      key: "recent",
      label: "Très actif",
      tone: "emerald",
      daysSince,
      lastCallLabel:
        daysSince === 0
          ? "Aujourd’hui"
          : `Il y a ${daysSince} jour${daysSince > 1 ? "s" : ""}`,
      sortValue: daysSince,
    };
  }

  if (daysSince <= 30) {
    return {
      key: "watch",
      label: "À surveiller",
      tone: "amber",
      daysSince,
      lastCallLabel: `Il y a ${daysSince} jours`,
      sortValue: daysSince,
    };
  }

  return {
    key: "inactive",
    label: "Inactif",
    tone: "rose",
    daysSince,
    lastCallLabel: `Il y a ${daysSince} jours`,
    sortValue: daysSince,
  };
}

const AttendanceStatsAdmin = () => {
  useScrollToTop();

  const [stats, setStats] = useState([]);
  const [chartData, setChartData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [chartPeriod, setChartPeriod] = useState("90");

  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await attendanceServices.getAttendanceStatsByClub();
      setStats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement stats :", error);
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChart = useCallback(async () => {
    try {
      setLoadingChart(true);
      const response = await attendanceServices.getAttendanceCountByDate();
      setChartData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur récupération des données de présence :", error);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchStats(), fetchChart()]);
    setLastUpdated(new Date());
  }, [fetchStats, fetchChart]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const enrichedStats = useMemo(() => {
    return stats.map((club) => {
      const totalCalls = Number(club?.nombre_appels_total || 0);
      const groupsCount = Number(club?.nombre_groupes || 0);
      const avgCallsPerGroup =
        groupsCount > 0 ? Number((totalCalls / groupsCount).toFixed(1)) : 0;

      return {
        ...club,
        totalCalls,
        groupsCount,
        avgCallsPerGroup,
        activity: getActivityMeta(club?.dernier_appel),
      };
    });
  }, [stats]);

  const filteredStats = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const out = enrichedStats.filter((club) => {
      const matchesSearch =
        !term ||
        String(club?.nom_club || "").toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === "all"
          ? true
          : club.activity.key === statusFilter;

      return matchesSearch && matchesStatus;
    });

    out.sort((a, b) => {
      switch (sortBy) {
        case "calls_desc":
          return b.totalCalls - a.totalCalls;

        case "groups_desc":
          return b.groupsCount - a.groupsCount;

        case "oldest":
          return b.activity.sortValue - a.activity.sortValue;

        case "name":
          return String(a.nom_club || "").localeCompare(
            String(b.nom_club || ""),
            "fr"
          );

        case "recent":
        default:
          return a.activity.sortValue - b.activity.sortValue;
      }
    });

    return out;
  }, [enrichedStats, searchTerm, statusFilter, sortBy]);

  const overviewItems = useMemo(() => {
    const totalClubs = enrichedStats.length;
    const recentClubs = enrichedStats.filter(
      (club) => club.activity.key === "recent"
    ).length;
    const watchClubs = enrichedStats.filter(
      (club) => club.activity.key === "watch"
    ).length;
    const inactiveClubs = enrichedStats.filter((club) =>
      ["inactive", "none"].includes(club.activity.key)
    ).length;
    const totalCalls = enrichedStats.reduce(
      (sum, club) => sum + club.totalCalls,
      0
    );
    const clubsNeverUsed = enrichedStats.filter(
      (club) => club.activity.key === "none"
    ).length;

    return [
      {
        label: "Clubs suivis",
        value: nf.format(totalClubs),
        icon: FaBuilding,
        tone: "blue",
        hint: "Clubs remontés dans les stats",
      },
      {
        label: "Très actifs",
        value: nf.format(recentClubs),
        icon: FaCalendarCheck,
        tone: "emerald",
        hint: "Dernier appel ≤ 7 jours",
      },
      {
        label: "À surveiller",
        value: nf.format(watchClubs),
        icon: FaClock,
        tone: "amber",
        hint: "Dernier appel entre 8 et 30 jours",
      },
      {
        label: "Inactifs",
        value: nf.format(inactiveClubs),
        icon: FaExclamationTriangle,
        tone: "rose",
        hint: "Plus de 30 jours ou aucun appel",
      },
      {
        label: "Appels totaux",
        value: nf.format(totalCalls),
        icon: FaUsers,
        tone: "violet",
        hint: "Historique cumulé",
      },
      {
        label: "Jamais utilisés",
        value: nf.format(clubsNeverUsed),
        icon: FaSearch,
        tone: "slate",
        hint: "Aucun appel trouvé",
      },
    ];
  }, [enrichedStats]);

  const clubsToWatch = useMemo(() => {
    return enrichedStats
      .filter((club) => ["watch", "inactive", "none"].includes(club.activity.key))
      .sort((a, b) => b.activity.sortValue - a.activity.sortValue)
      .slice(0, 4);
  }, [enrichedStats]);

  return (
    <div className="p-4 mt-[100px] w-full mx-auto font-satoshi">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="rounded-[28px] overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-500 px-6 py-7 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Suivi des appels de présence
                </h1>
                <p className="mt-2 text-sm md:text-base text-white/90 max-w-3xl">
                  Identifie les clubs actifs, ceux à relancer et la dynamique
                  globale des appels de présence sur l’application.
                </p>
                <p className="mt-3 text-xs text-white/80">
                  {lastUpdated
                    ? `Dernière mise à jour : ${dayjs(lastUpdated).format(
                        "DD/MM/YYYY à HH:mm"
                      )}`
                    : "Chargement des données..."}
                </p>
              </div>

              <button
                onClick={refreshAll}
                className="inline-flex items-center gap-2 rounded-2xl bg-white/15 hover:bg-white/25 px-4 py-2.5 text-white transition"
              >
                <FaSyncAlt className={cn((loading || loadingChart) && "animate-spin")} />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            <AttendanceOverviewStats items={overviewItems} />
          </div>
        </div>

        <AttendanceChart
          data={chartData}
          loading={loadingChart}
          period={chartPeriod}
          setPeriod={setChartPeriod}
        />

        {clubsToWatch.length > 0 ? (
          <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Clubs à surveiller en priorité
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Clubs sans activité récente ou avec un dernier appel ancien.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {clubsToWatch.map((club) => (
                <AttendanceClubCard key={`watch-${club.club_id}`} club={club} compact />
              ))}
            </div>
          </div>
        ) : null}

        <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
          <AttendanceFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            totalResults={filteredStats.length}
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-48 rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm animate-pulse"
              />
            ))}
          </div>
        ) : filteredStats.length === 0 ? (
          <div className="rounded-[28px] border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Aucune donnée disponible
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              Aucun club ne correspond aux filtres sélectionnés.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {filteredStats.map((club) => (
              <AttendanceClubCard
                key={club.club_id}
                club={club}
                lastCallFormatted={formatDate(club.dernier_appel)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceStatsAdmin;